import axiosInstance from "../services/AxiosInstance";

const API_URL = "/classroom-management";

// Full per-room / per-trainer schedule for a date range, fully resolved
// server-side (branch/trainer/course names, occupancy status, AND every
// filter — branch/floor/status/search all narrow the `rooms` array on the
// server; nothing is filtered client-side). `fromDate` is required; `toDate`
// defaults to `fromDate` (single day). `fromTime`/`toTime` ("HH:mm") are
// optional — when given, status reflects whether a session overlaps that
// clock-time window on any date in range, instead of "has any session that
// day". Passing the same value for both is treated as an exact instant check
// (occupancy right at that clock time).
export const getSchedule = async ({ fromDate, toDate, fromTime, toTime, branch, floor, status, search } = {}) => {
  try {
    const response = await axiosInstance.get(`${API_URL}/schedule`, {
      params: {
        fromDate,
        toDate: toDate || undefined,
        fromTime: fromTime || undefined,
        toTime: toTime || undefined,
        branch: branch || undefined,
        floor: floor || undefined,
        status: status || undefined,
        search: search || undefined,
      },
      // This aggregates across 7 separate collections and can span a wide
      // multi-day range (Labs/Trainers timeline grid view), so it's
      // genuinely slower than the app-wide 15s default — especially right
      // after the API process starts, before its DB connections are warm.
      // Overriding it here (rather than raising the shared instance default)
      // keeps every other call's timeout untouched.
      timeout: 45000,
    });
    return response.data.data;
  } catch (error) {
    console.error("Error fetching classroom schedule", error);
    throw error;
  }
};

// Labs-only and Trainers-only variants of getSchedule — same params, but
// each only asks the server to build its own half (rooms, or trainers),
// skipping the other one entirely. Used by the Labs/Trainers timeline views;
// getSchedule (both combined) stays reserved for Overview, which shows both
// at once and needs them from one consistent snapshot.
//
// `days` — a weekday filter, array of "monday".."sunday" (matches the batch
// `days` field's own values). Only sessions actually falling on one of the
// picked weekdays count; empty/omitted means no day filtering, same as
// every other filter here.
export const getScheduleLabs = async ({ fromDate, toDate, fromTime, toTime, days, department, branch, floor, status, search } = {}) => {
  try {
    const response = await axiosInstance.get(`${API_URL}/schedule/labs`, {
      params: {
        fromDate,
        toDate: toDate || undefined,
        fromTime: fromTime || undefined,
        toTime: toTime || undefined,
        days: days?.length ? days.join(",") : undefined,
        department: department || undefined,
        branch: branch || undefined,
        floor: floor || undefined,
        status: status || undefined,
        search: search || undefined,
      },
      timeout: 45000,
    });
    return response.data.data;
  } catch (error) {
    console.error("Error fetching labs schedule", error);
    throw error;
  }
};

export const getScheduleTrainers = async ({ fromDate, toDate, fromTime, toTime, days, department, search } = {}) => {
  try {
    const response = await axiosInstance.get(`${API_URL}/schedule/trainers`, {
      params: {
        fromDate,
        toDate: toDate || undefined,
        fromTime: fromTime || undefined,
        toTime: toTime || undefined,
        days: days?.length ? days.join(",") : undefined,
        department: department || undefined,
        search: search || undefined,
      },
      timeout: 45000,
    });
    return response.data.data;
  } catch (error) {
    console.error("Error fetching trainers schedule", error);
    throw error;
  }
};

// The Labs / Trainers timeline for one month. `month` is "YYYY-MM" (default
// this month on the server), `kind` is "labs" or "trainers". Returns the
// days and one row per lab or trainer, every cell resolved: status, label,
// that day's sessions and free windows. The page only draws them.
export const getScheduleMonth = async ({ month, kind, fromTime, toTime, days, department, branch, floor, search } = {}) => {
  try {
    const response = await axiosInstance.get(`${API_URL}/schedule/month`, {
      params: {
        month: month || undefined,
        kind: kind || undefined,
        fromTime: fromTime || undefined,
        toTime: toTime || undefined,
        days: days?.length ? days.join(",") : undefined,
        department: department || undefined,
        branch: branch || undefined,
        floor: floor || undefined,
        search: search || undefined,
      },
      timeout: 45000,
    });
    return response.data.data;
  } catch (error) {
    console.error("Error fetching the month timeline", error);
    throw error;
  }
};

// The day grid for one `date`: half-hour slots down, labs across (`kind`
// "labs") or trainers across (`kind` "trainers"), every cell resolved on
// the server (batches, clash, over capacity).
export const getScheduleSlots = async ({ kind, date, fromTime, toTime, days, department, branch, floor, status, search } = {}) => {
  try {
    const response = await axiosInstance.get(`${API_URL}/schedule/slots`, {
      params: {
        kind: kind || undefined,
        date: date || undefined,
        fromTime: fromTime || undefined,
        toTime: toTime || undefined,
        days: days?.length ? days.join(",") : undefined,
        department: department || undefined,
        branch: branch || undefined,
        floor: floor || undefined,
        status: status || undefined,
        search: search || undefined,
      },
      timeout: 45000,
    });
    return response.data.data;
  } catch (error) {
    console.error("Error fetching the lab grid", error);
    throw error;
  }
};

// Report tiles + rows for a date range, fully resolved server-side.
export const getReport = async (from, to, view, status = "") => {
  try {
    const response = await axiosInstance.get(`${API_URL}/reports`, { params: { from, to, view, status } });
    return response.data.data;
  } catch (error) {
    console.error("Error fetching classroom report", error);
    throw error;
  }
};

// Batches joined with trainerName/courseName/classroomNo. Server-paginated + searched.
export const getBookingsList = async (page = 1, limit = 20, search = "") => {
  try {
    const response = await axiosInstance.get(`${API_URL}/bookings`, { params: { page, limit, search } });
    return { data: response.data.data, pagination: response.data.pagination };
  } catch (error) {
    console.error("Error fetching bookings list", error);
    throw error;
  }
};

// Classrooms joined with branchName. Server-paginated + searched.
export const getLabsList = async (page = 1, limit = 20, search = "", status = "") => {
  try {
    const response = await axiosInstance.get(`${API_URL}/labs`, { params: { page, limit, search, status } });
    return { data: response.data.data, pagination: response.data.pagination };
  } catch (error) {
    console.error("Error fetching labs list", error);
    throw error;
  }
};

// Trainers joined with batchesCount. Server-paginated + searched.
export const getTrainersOverview = async (page = 1, limit = 20, search = "") => {
  try {
    const response = await axiosInstance.get(`${API_URL}/trainers-overview`, { params: { page, limit, search } });
    return { data: response.data.data, pagination: response.data.pagination };
  } catch (error) {
    console.error("Error fetching trainers overview", error);
    throw error;
  }
};

// Trainers with today's Free/Busy state -- Busy when a batch of theirs is
// running today. The availability filter ("all" | "free" | "busy"), search
// and pagination are all applied server-side; `summary` is the API's
// { asOf, all, free, busy } counts for the same search.
export const getTrainersAvailability = async ({ page = 1, limit = 20, search = "", availability = "all" } = {}) => {
  try {
    const response = await axiosInstance.get(`${API_URL}/trainers-availability`, {
      params: { page, limit, search, availability },
    });
    return {
      data: response.data.data,
      pagination: response.data.pagination,
      summary: response.data.meta || null,
    };
  } catch (error) {
    console.error("Error fetching trainers availability", error);
    throw error;
  }
};

// One trainer in full for the Trainers tab's view popup: the record with
// today's Free/Busy, batch counts by status, and their batches -- sorted,
// searched (`search`: batch or course name) and capped server-side.
export const getTrainerDetail = async (id, { search = "", limit } = {}) => {
  try {
    const response = await axiosInstance.get(`${API_URL}/trainer-detail/${encodeURIComponent(id)}`, {
      params: { ...(search ? { search } : {}), ...(limit ? { limit } : {}) },
    });
    return response.data.data;
  } catch (error) {
    console.error("Error fetching trainer detail", error);
    throw error;
  }
};

// Batch overrides joined with batchName/classroomNo/newClassroomNo. Server-paginated + searched.
export const getOverridesList = async (page = 1, limit = 20, search = "") => {
  try {
    const response = await axiosInstance.get(`${API_URL}/overrides`, { params: { page, limit, search } });
    return { data: response.data.data, pagination: response.data.pagination };
  } catch (error) {
    console.error("Error fetching overrides list", error);
    throw error;
  }
};

// Trainer leaves joined with trainerName. Server-paginated + searched.
export const getLeavesList = async (page = 1, limit = 20, search = "") => {
  try {
    const response = await axiosInstance.get(`${API_URL}/leaves`, { params: { page, limit, search } });
    return { data: response.data.data, pagination: response.data.pagination };
  } catch (error) {
    console.error("Error fetching leaves list", error);
    throw error;
  }
};

// Running batches already saved into one lab at the same time, each pair
// with where they meet -- the dashboard's "Lab overlaps" figure and list.
export const getLabOverlaps = async () => {
  try {
    const response = await axiosInstance.get(`${API_URL}/lab-overlaps`);
    return response.data.data || { overlaps: [], count: 0 };
  } catch (error) {
    console.error("Error fetching lab overlaps", error);
    throw error;
  }
};

// Server-side clash check for a candidate booking (create or edit). Each
// clash has `lab` (same lab, same time -- the save is refused) and/or
// `trainer` (same trainer -- a warning only). Silent:
// it runs in the background as the form's dates, days and times change, so
// it must never raise the global "Working" overlay.
export const checkClash = async ({ trainerId, classroom, days, startTime, endTime, excludeBatchId }) => {
  try {
    const response = await axiosInstance.post(`${API_URL}/check-clash`, {
      trainerId,
      classroom,
      days,
      startTime,
      endTime,
      excludeBatchId,
    }, { silent: true });
    return response.data.data.clashes || [];
  } catch (error) {
    console.error("Error checking booking clash", error);
    throw error;
  }
};
